import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import ForceGraph3DLib from 'react-force-graph-3d';
import * as THREE from 'three';

const NODE_COLORS = {
  SERVICE: '#3b82f6',
  TEAM: '#22c55e',
  PATTERN: '#a855f7',
  API: '#eab308',
  REGULATION: '#ef4444',
  DOCUMENT: '#06b6d4',
  CAPABILITY: '#f97316',
  PRODUCT: '#ec4899',
  DEFAULT: '#6b7280',
};

// Sphere radius for globe
const GLOBE_RADIUS = 150;

function GlobeGraph({ entities, relationships, selectedEntity, onSelectEntity, width, height }) {
  const fgRef = useRef();

  const graphData = useMemo(() => {
    // Distribute nodes on sphere surface using fibonacci sphere
    const n = entities.length;
    const goldenRatio = (1 + Math.sqrt(5)) / 2;

    const nodes = entities.map((entity, i) => {
      // Fibonacci sphere distribution
      const theta = 2 * Math.PI * i / goldenRatio;
      const phi = Math.acos(1 - 2 * (i + 0.5) / n);

      // Convert spherical to cartesian
      const x = GLOBE_RADIUS * Math.sin(phi) * Math.cos(theta);
      const y = GLOBE_RADIUS * Math.sin(phi) * Math.sin(theta);
      const z = GLOBE_RADIUS * Math.cos(phi);

      return {
        id: entity.id,
        name: entity.name,
        type: entity.entity_type,
        description: entity.description,
        color: NODE_COLORS[entity.entity_type] || NODE_COLORS.DEFAULT,
        // Fixed positions on sphere
        fx: x,
        fy: y,
        fz: z,
        ...entity,
      };
    });

    const nodeIds = new Set(nodes.map(n => n.id));
    const links = relationships
      .filter(rel => nodeIds.has(rel.source_id) && nodeIds.has(rel.target_id))
      .map(rel => ({
        source: rel.source_id,
        target: rel.target_id,
        type: rel.rel_type,
      }));

    return { nodes, links };
  }, [entities, relationships]);

  // Add globe wireframe
  useEffect(() => {
    if (!fgRef.current) return;

    const scene = fgRef.current.scene();

    // Remove old globe if exists
    const oldGlobe = scene.getObjectByName('globeWireframe');
    if (oldGlobe) scene.remove(oldGlobe);

    // Create wireframe sphere
    const geometry = new THREE.SphereGeometry(GLOBE_RADIUS - 5, 32, 24);
    const material = new THREE.MeshBasicMaterial({
      color: 0x1f2937,
      wireframe: true,
      transparent: true,
      opacity: 0.3,
    });
    const globe = new THREE.Mesh(geometry, material);
    globe.name = 'globeWireframe';
    scene.add(globe);

    // Add latitude/longitude lines
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x374151,
      transparent: true,
      opacity: 0.2
    });

    // Equator and key latitudes
    [0, 30, -30, 60, -60].forEach(lat => {
      const latRad = lat * Math.PI / 180;
      const r = GLOBE_RADIUS * Math.cos(latRad);
      const y = GLOBE_RADIUS * Math.sin(latRad);
      const points = [];
      for (let i = 0; i <= 64; i++) {
        const theta = (i / 64) * 2 * Math.PI;
        points.push(new THREE.Vector3(r * Math.cos(theta), y, r * Math.sin(theta)));
      }
      const lineGeom = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(lineGeom, lineMaterial);
      line.name = 'globeWireframe';
      scene.add(line);
    });

    // Meridians
    for (let i = 0; i < 12; i++) {
      const theta = (i / 12) * 2 * Math.PI;
      const points = [];
      for (let j = 0; j <= 32; j++) {
        const phi = (j / 32) * Math.PI;
        points.push(new THREE.Vector3(
          GLOBE_RADIUS * Math.sin(phi) * Math.cos(theta),
          GLOBE_RADIUS * Math.cos(phi),
          GLOBE_RADIUS * Math.sin(phi) * Math.sin(theta)
        ));
      }
      const lineGeom = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(lineGeom, lineMaterial);
      line.name = 'globeWireframe';
      scene.add(line);
    }

    // Auto-rotate the camera
    let animationId;
    const rotate = () => {
      if (fgRef.current) {
        const camera = fgRef.current.camera();
        const distance = camera.position.length();
        const time = Date.now() * 0.0001;
        // Slow gentle rotation
        // camera.position.x = distance * Math.sin(time);
        // camera.position.z = distance * Math.cos(time);
        // camera.lookAt(0, 0, 0);
      }
      animationId = requestAnimationFrame(rotate);
    };
    // rotate(); // Uncomment for auto-rotation

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [graphData]);

  // Custom node object
  const nodeThreeObject = useCallback((node) => {
    const isSelected = node.id === selectedEntity;
    const group = new THREE.Group();

    // Sphere node
    const geometry = new THREE.SphereGeometry(isSelected ? 5 : 4, 16, 16);
    const material = new THREE.MeshLambertMaterial({
      color: node.color,
      emissive: node.color,
      emissiveIntensity: 0.3,
    });
    const sphere = new THREE.Mesh(geometry, material);
    group.add(sphere);

    // Glow for selected
    if (isSelected) {
      const glowGeom = new THREE.SphereGeometry(8, 16, 16);
      const glowMat = new THREE.MeshBasicMaterial({
        color: '#a855f7',
        transparent: true,
        opacity: 0.4,
      });
      group.add(new THREE.Mesh(glowGeom, glowMat));
    }

    // Label sprite
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 200;
    canvas.height = 50;

    ctx.fillStyle = 'rgba(10, 10, 10, 0.8)';
    ctx.beginPath();
    ctx.roundRect(0, 0, 200, 50, 8);
    ctx.fill();

    ctx.fillStyle = node.color;
    ctx.beginPath();
    ctx.arc(20, 25, 10, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#fff';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(node.type?.charAt(0) || '?', 20, 29);

    ctx.fillStyle = '#e5e5e5';
    ctx.font = '14px Arial';
    ctx.textAlign = 'left';
    const name = node.name?.length > 12 ? node.name.substring(0, 12) + '..' : node.name;
    ctx.fillText(name || '', 38, 30);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMat = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(spriteMat);
    sprite.scale.set(20, 5, 1);
    sprite.position.set(0, 8, 0);
    group.add(sprite);

    return group;
  }, [selectedEntity]);

  // Link rendering - curved along sphere surface
  const linkPositionUpdate = useCallback((link, { start, end }) => {
    // Links follow great circle path (curved on sphere)
    const startVec = new THREE.Vector3(start.x, start.y, start.z);
    const endVec = new THREE.Vector3(end.x, end.y, end.z);

    // Just use straight lines for now - curved great circles are complex
    return true;
  }, []);

  const handleNodeClick = useCallback((node) => {
    onSelectEntity(node.id);

    // Zoom to node
    if (fgRef.current) {
      const distance = 250;
      const distRatio = 1 + distance / Math.hypot(node.fx, node.fy, node.fz);
      fgRef.current.cameraPosition(
        { x: node.fx * distRatio, y: node.fy * distRatio, z: node.fz * distRatio },
        { x: node.fx, y: node.fy, z: node.fz },
        1000
      );
    }
  }, [onSelectEntity]);

  return (
    <ForceGraph3DLib
      ref={fgRef}
      width={width}
      height={height}
      graphData={graphData}
      nodeThreeObject={nodeThreeObject}
      nodeThreeObjectExtend={false}
      linkColor={() => '#4b5563'}
      linkOpacity={0.4}
      linkWidth={1}
      linkCurvature={0.2}
      onNodeClick={handleNodeClick}
      onBackgroundClick={() => onSelectEntity(null)}
      backgroundColor="#0a0a0a"
      showNavInfo={false}
      enableNodeDrag={false}
      enableNavigationControls={true}
      controlType="orbit"
    />
  );
}

export default GlobeGraph;
